import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

export interface Timezone {
  country: string;
  label: string;
  value: string;
}

export interface OptionsGroup {
  label: string;
  options: Timezone[];
}

@DecorateUntilDestroy()
@Component({
  selector: 'crm-search-field',
  templateUrl: 'search-field.component.html',
  styleUrls: ['search-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchFieldComponent),
      multi: true,
    },
  ],
})
export class SearchFieldComponent implements OnInit, ControlValueAccessor {
  @Input() readonly = false;
  @Input() placeholder: string;
  @Input() placeholderLabel: string;
  @Input() noEntriesFoundLabel: string;

  @Input() searchCallbackFn: (substr: string) => {};

  @Input() set options(options: OptionsGroup[]) {
    this.optionsSubject.next(options);
  }

  private optionsSubject = new BehaviorSubject<OptionsGroup[]>([]);
  options$: Observable<OptionsGroup[]>;
  searchControl: UntypedFormControl = new UntypedFormControl();
  valuesControl: UntypedFormControl = new UntypedFormControl();

  ngOnInit(): void {
    const searchTerm$ = this.searchControl.valueChanges.pipe(startWith(''));

    this.options$ = combineLatest([this.optionsSubject.asObservable(), searchTerm$]).pipe(
      debounceTime(300),
      map(([rawOptions, term]) => this.filterBankGroups(rawOptions, term)),
    );

    this.valuesControl.valueChanges.pipe(takeUntilDestroyed(this)).subscribe(value => {
      if (Array.isArray(value) && value.length === 0) {
        value = null;
      }
      this.onChange(value);
      this.onTouched();
    });

    if (!this.optionsSubject.getValue()) {
      this.searchControl.patchValue({});
    }
  }

  protected filterBankGroups(options: OptionsGroup[], term: string): OptionsGroup[] {
    term = term.toLowerCase();
    const groupsCopy = this.copyGroups(options);

    return term
      ? groupsCopy.filter((searchGroup: OptionsGroup) => {
          const showGroup = searchGroup.label.toLowerCase().includes(term);
          if (!showGroup) {
            searchGroup.options = searchGroup.options.filter((timezone: Timezone) => {
              return timezone.label.toLowerCase().includes(term);
            });
          }
          return searchGroup.options.length > 0;
        })
      : options;
  }

  protected copyGroups(options: OptionsGroup[]): OptionsGroup[] {
    const groupsCopy = [];
    options.forEach((group: OptionsGroup) => {
      groupsCopy.push({
        label: group.label,
        options: group.options.slice(),
      });
    });
    return groupsCopy;
  }

  writeValue(value: unknown): void {
    this.valuesControl.setValue(value);
  }

  private onChange = (_: unknown): void => {};

  private onTouched = (): void => {};

  registerOnChange(fn: (_: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
